import { useState, useEffect, useMemo, useRef } from 'react';
import {
    Alert,
    Badge,
    Box,
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Tooltip as MuiTooltip,
    Checkbox,
    Modal,
    Button as MuiButton,
} from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams } from 'react-router-dom';
import { getSOADetailsByToken, getSignedUrl, getCompanyByPropertyId } from '../api';
import {
    Button,
    Container,
    Progress,
} from 'reactstrap';
import jwtDecode from 'jwt-decode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './soa.scss';
import { getSOAInitialValues } from '../Generate/Components/FormikHelpers';
import DeductionInvoiceRef from '../Generate/Components/DeductionInvoiceRef';
import { AttachmentsSOA } from './AttachmentsSOA';
import { format, parseISO } from 'date-fns';

const formatAmount = (value) => {
    if (!value) return '$0.00';
    const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
    const absValue = Math.abs(numValue).toFixed(2);
    return numValue < 0 ? `-$${absValue}` : `$${absValue}`;
};

const extractS3KeyAndType = (invoice) => {
    if (!invoice) return { key: '', fileType: '' };

    if (typeof invoice === 'string') {
        const urlPatterns = [
            /^https?:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/,
            /^https?:\/\/rentdigicare\.tor1\.digitaloceanspaces\.com\/(.+)$/,
            /^https?:\/\/[^\/]+\/[^\/]+\/(.+)$/,
        ];

        let key = invoice;

        for (const pattern of urlPatterns) {
            const match = invoice.match(pattern);
            if (match) {
                key = match[1];
                break;
            }
        }

        if (key === invoice && invoice.startsWith('http')) {
            key = invoice.replace(/^https?:\/\/[^\/]+\//, '');
        }

        key = decodeURIComponent(key);

        const fileType = key.split('.').pop()?.toLowerCase() || '';
        return { key, fileType };
    }

    let key = invoice.url || '';

    if (key.startsWith('http')) {
        const urlPatterns = [
            /^https?:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/,
            /^https?:\/\/rentdigicare\.tor1\.digitaloceanspaces\.com\/(.+)$/,
        ];

        for (const pattern of urlPatterns) {
            const match = key.match(pattern);
            if (match) {
                key = decodeURIComponent(match[1]);
                break;
            }
        }

        if (key.startsWith('http')) {
            key = decodeURIComponent(key.replace(/^https?:\/\/[^\/]+\//, ''));
        }
    }

    return {
        key,
        fileType: invoice.fileType?.toLowerCase() || key.split('.').pop()?.toLowerCase() || '',
    };
};

const formatDateSafely = (dateValue) => {
    if (!dateValue) return 'N/A';

    let date;

    if (dateValue instanceof Date) {
        date = dateValue;
    } else {
        const str = dateValue.toString().trim();
        // Case 1: Full ISO with Z (UTC) → e.g. "2025-06-01T00:00:00.000Z"
        if (str.includes('T') && str.endsWith('Z')) {
            date = parseISO(str); // Correctly parses UTC
        }
        // Case 2: Date-only string → "2025-06-01"
        else if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
            // Treat as local date at midnight (prevents shift)
            date = new Date(str); // This is safe for YYYY-MM-DD format
            // OR better: reconstruct to avoid any offset
            const [y, m, d] = str.split('-');
            date = new Date(Number(y), Number(m) - 1, Number(d));
        }
        // Case 3: Includes time but no Z → assume it's in desired timezone (e.g. EST/EDT)
        else if (str.includes('T')) {
            date = parseISO(str);
        } else {
            return 'Invalid Date';
        }
    }

    // Ensure the date is treated as local (no timezone conversion)
    return format(date, 'yyyy-MM-dd');
};

const ReadOnlyField = ({ label, value, icon, className = '' }) => {
    const formattedValue = useMemo(() => {
        if (value && (label === 'Start Date' || label === 'End Date' || label === 'Date Mailed')) {
            try {
                return formatDateSafely(value);
            } catch {
                return 'Invalid Date';
            }
        }
        return value || 'N/A';
    }, [value, label]);

    return (
        <>
            <Box className={`soa-editable-field ${className}`} sx={{ width: '100%' }}>
                <Box className="soa-display-value" sx={{ width: '100%' }}>
                    <Typography variant="body2" className="soa-field-text">
                        {formattedValue}
                    </Typography>
                </Box>
            </Box>
            {label && (
                <Typography
                    variant="caption"
                    className="soa-field-subtitle"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}
                >
                    {icon && <FeatherIcon icon={icon} size={16} className="soa-edit-icon" />}
                    {`(${label})`}
                </Typography>
            )}
        </>
    );
};

const PreviewSOA = ({
    soaId: propSoaId,
    token: propToken,
    showInvoices = false,
    showDownloadButtons = true,
    showAttachments = true,
}) => {
    const params = useParams();
    const token = propToken || params.token;
    const soaId = propSoaId || (token ? jwtDecode(token)?.soaId : null);

    const [soaData, setSOAData] = useState(null);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showProgress, setShowProgress] = useState(false);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [modalContent, setModalContent] = useState({ url: '', description: '', fileType: '' });
    const soaFormRef = useRef(null);

    useEffect(() => {
        const fetchCompanyDetails = async () => {
            try {
                const propertyId = soaData?.property_details?.property_id;
                if (propertyId) {
                    const response = await getCompanyByPropertyId(propertyId);
                    setCompanyDetails(response);
                }
            } catch (error) {
                console.error('Failed to fetch company details:', error);
            }
        };
        if (soaData?.property_details?.property_id) {
            fetchCompanyDetails();
        }
    }, [soaData?.property_details?.property_id]);

    useEffect(() => {
        const fetchSOAData = async () => {
            try {
                setLoading(true);
                const args = token ? [soaId, token] : [soaId];
                const transformedData = await getSOAInitialValues(...args);
                const rawResponse = await getSOADetailsByToken(...args);

                if (!rawResponse.success) {
                    setError(rawResponse.message || 'Failed to load SOA details.');
                    return;
                }

                setSOAData(transformedData);
            } catch (err) {
                console.error('Error fetching SOA data:', err);
                setError(err.message || 'Failed to load SOA details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (soaId) {
            fetchSOAData();
        }
    }, [soaId, token]);

    const totalDeductions = useMemo(() => {
        if (!soaData?.account_details?.deductions) return 0;
        return soaData.account_details.deductions.reduce(
            (sum, deduction) => sum + (Number(deduction.cost_per_action) || 0) * (Number(deduction.number_of_actions) || 1),
            0
        );
    }, [soaData?.account_details?.deductions]);

    const balance = useMemo(() => {
        const totalSecurity = Number(soaData?.account_details?.security) || 0;
        const totalInterest = Number(soaData?.account_details?.interest) || 0;
        const earlyMoveOutProrate = Number(soaData?.account_details?.early_move_out_prorate) || 0;
        return totalSecurity + totalInterest + earlyMoveOutProrate - totalDeductions;
    }, [soaData?.account_details?.security, soaData?.account_details?.interest, soaData?.account_details?.early_move_out_prorate, totalDeductions]);

    const handleViewInvoice = async (index) => {
        const deductions = soaData.account_details?.deductions || [];
        if (!deductions || index < 0 || index >= deductions.length) {
            alert('Invalid deduction selected.');
            return;
        }

        const deduction = deductions[index];
        if (!deduction.invoice || (typeof deduction.invoice === 'string' && deduction.invoice.trim() === '')) {
            alert('No invoice available to view.');
            return;
        }

        try {
            const { key, fileType } = extractS3KeyAndType(deduction.invoice);
            if (!key) throw new Error('Invalid invoice key');
            const response = await getSignedUrl(key, 'get', fileType);
            if (response.success && response.data.signedUrl) {
                setModalContent({
                    url: response.data.signedUrl,
                    description: deduction.invoice?.description || '',
                    fileType,
                });
                setOpenModal(true);
            } else {
                alert('Failed to load the document for preview.');
            }
        } catch (error) {
            console.error('Error viewing invoice:', error);
            alert('Failed to load the document for preview.');
        }
    };

    const handleDownloadInvoice = async () => {
        try {
            const { url, fileType } = modalContent;
            if (!url) {
                alert('No invoice available to download.');
                return;
            }

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch invoice');
            const blob = await response.blob();
            const fileName = `invoice.${fileType || 'pdf'}`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error downloading invoice:', error);
            alert('Failed to download the invoice. Please try again.');
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setModalContent({ url: '', description: '', fileType: '' });
    };

    const generatePDFFile = async (showProgressUpdates = false) => {
        if (!soaFormRef.current) return null;

        try {
            if (showProgressUpdates) {
                setShowProgress(true);
                setProgressMessage('Preparing document...');
                setProgress(5);
            }

            const pdf = new jsPDF('p', 'mm', 'a4');
            const soaElement = soaFormRef.current;

            if (showProgressUpdates) {
                setProgressMessage('Capturing document...');
                setProgress(25);
            }

            const canvas = await html2canvas(soaElement, {
                scale: 2,
                useCORS: true,
                logging: false,
                allowTaint: true,
                backgroundColor: '#ffffff',
                removeContainer: true,
                imageTimeout: 15000,
                height: soaElement.scrollHeight,
                width: soaElement.scrollWidth,
            });

            if (showProgressUpdates) {
                setProgressMessage('Processing image...');
                setProgress(50);
            }

            const imgData = canvas.toDataURL('image/jpeg', 0.9);
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const sidePadding = 10;
            const topBottomPadding = 5;
            const availableWidth = pdfWidth - (sidePadding * 2);
            const availableHeight = pdfHeight - (topBottomPadding * 2);
            const imgWidth = imgProps.width;
            const imgHeight = imgProps.height;
            const ratio = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);
            const scaledWidth = imgWidth * ratio;
            const scaledHeight = imgHeight * ratio;
            const xOffset = sidePadding + (availableWidth - scaledWidth) / 2;
            const yOffset = topBottomPadding;

            if (showProgressUpdates) {
                setProgressMessage('Adding to PDF...');
                setProgress(75);
            }

            const pageHeight = availableHeight;
            const totalPages = Math.ceil(scaledHeight / pageHeight);

            for (let i = 0; i < totalPages; i++) {
                if (i > 0) {
                    pdf.addPage();
                }
                const sourceY = i * (pageHeight / ratio);
                const sourceHeight = Math.min(pageHeight / ratio, imgHeight - sourceY);

                if (sourceHeight > 0) {
                    const pageCanvas = document.createElement('canvas');
                    const pageCtx = pageCanvas.getContext('2d');
                    const img = new Image();
                    await new Promise((resolve) => {
                        img.onload = () => {
                            pageCanvas.width = imgWidth;
                            pageCanvas.height = sourceHeight;
                            pageCtx.drawImage(img, 0, -sourceY);
                            const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.9);
                            pdf.addImage(pageImgData, 'JPEG', xOffset, yOffset, scaledWidth, sourceHeight * ratio);
                            resolve();
                        };
                        img.src = imgData;
                    });
                }
            }

            if (showProgressUpdates) {
                setProgressMessage('Finalizing PDF...');
                setProgress(90);
            }

            const pdfBlob = pdf.output('blob');
            const pdfFile = new File([pdfBlob], 'SOA-Statement.pdf', { type: 'application/pdf' });

            if (showProgressUpdates) {
                setProgress(100);
                setProgressMessage('PDF generated successfully!');
                await new Promise((resolve) => setTimeout(resolve, 500));
            }

            return pdfFile;
        } catch (error) {
            console.error('Error generating PDF:', error);
            if (showProgressUpdates) {
                setProgressMessage('Error generating PDF.');
            }
            throw error;
        } finally {
            if (showProgressUpdates) {
                setTimeout(() => {
                    setShowProgress(false);
                    setProgress(0);
                    setProgressMessage('');
                }, 1000);
            }
        }
    };

    const handlePrint = async () => {
        try {
            const pdfFile = await generatePDFFile(true);
            if (pdfFile) {
                const url = URL.createObjectURL(pdfFile);
                const printWindow = window.open(url, '_blank');
                if (printWindow) {
                    printWindow.onload = () => {
                        printWindow.print();
                        setTimeout(() => URL.revokeObjectURL(url), 1000);
                    };
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'SOA-Statement.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }
            }
        } catch (error) {
            console.error('Print failed:', error);
            alert('Failed to generate PDF for printing. Please try again.');
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const pdfFile = await generatePDFFile(true);
            if (pdfFile) {
                const url = URL.createObjectURL(pdfFile);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'SOA-Statement.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error('Download failed:', error);
            alert('Failed to generate PDF for download. Please try again.');
        }
    };

    if (loading) {
        return (
            <Box className="soa-form-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></Box>
                    <Typography variant="body1">Loading SOA details...</Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="soa-form-container" sx={{ maxWidth: '960px', mx: 'auto', p: 3 }}>
                <Paper elevation={2} className="soa-document-wrapper">
                    <Alert severity="error" icon={<FeatherIcon icon="x" size={20} />}>
                        <Typography variant="h6">Error</Typography>
                        <Typography variant="body2">{error}</Typography>
                    </Alert>
                </Paper>
            </Box>
        );
    }

    if (!soaData) {
        return (
            <Box className="soa-form-container" sx={{ maxWidth: '960px', mx: 'auto', p: 3 }}>
                <Paper elevation={2} className="soa-document-wrapper">
                    <Alert severity="warning" icon={<FeatherIcon icon="file-text" size={20} />}>
                        <Typography variant="h6">Warning</Typography>
                        <Typography variant="body2">SOA not found.</Typography>
                    </Alert>
                </Paper>
            </Box>
        );
    }

    const tenantDisplay = soaData.tenant
        ?.map(t => {
            const name = t.name || '';
            const email = t.email && t.email.toLowerCase() !== 'n/a' ? t.email : '';
            return email ? `${name} (${email})` : name;
        })
        .filter(Boolean)
        .join(', ') || '';

    const displayDeductions = soaData?.account_details?.deductions || [];

    return (
        <Box className="soa-form-container">
            {showProgress && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            minWidth: 300,
                            textAlign: 'center',
                        }}
                    >
                        <Box className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" sx={{ mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {progressMessage}
                        </Typography>
                        <Box sx={{ width: '100%', mb: 2 }}>
                            <Progress value={progress} color="primary" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {progress}% Complete
                        </Typography>
                    </Paper>
                </Box>
            )}

            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="invoice-modal-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    sx={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        width: '800px',
                        p: 4,
                        overflow: 'auto',
                        position: 'relative',
                    }}
                >
                    <Typography id="invoice-modal-title" variant="h6" sx={{ mb: 2 }}>
                        Invoice Preview
                    </Typography>
                    {modalContent.description && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                Description:
                            </Typography>
                            <Typography variant="body2">{modalContent.description}</Typography>
                        </Box>
                    )}
                    {modalContent.fileType === 'pdf' ? (
                        <iframe
                            src={modalContent.url}
                            style={{ width: '100%', height: '500px', border: 'none' }}
                            title="Invoice Preview"
                        />
                    ) : (
                        <img
                            src={modalContent.url}
                            alt="Invoice"
                            style={{ width: '100%', maxHeight: '500px', objectFit: 'contain' }}
                        />
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <MuiButton
                            variant="contained"
                            color="primary"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadInvoice}
                        >
                            Download Invoice
                        </MuiButton>
                        <MuiButton variant="outlined" onClick={handleCloseModal}>
                            Close
                        </MuiButton>
                    </Box>
                </Paper>
            </Modal>

            <Paper elevation={2} className="soa-document-wrapper">
                <div ref={soaFormRef}>
                    <Box className="soa-document-header">
                        {companyDetails && (
                            <>
                                {companyDetails.logo && (
                                    <img
                                        src={companyDetails.logo}
                                        alt={`${companyDetails.name} Logo`}
                                        className="soa-company-logo"
                                        style={{ maxHeight: '50px', maxWidth: '150px', marginBottom: '10px' }}
                                    />
                                )}
                                <Typography
                                    variant="h5"
                                    className="soa-company-name"
                                    sx={{ fontWeight: 'bold', marginBottom: '10px' }}
                                >
                                    {companyDetails.name}
                                </Typography>
                            </>
                        )}
                        <Typography variant="h4" className="soa-main-title">
                            Landlord's Notice to Tenant
                        </Typography>
                        <Typography variant="h3" className="soa-document-title">
                            Security Deposit — Statement of Account
                        </Typography>
                        <Typography variant="body2" className="soa-legal-notice">
                            This must be forwarded to tenant within 10 days of the tenant giving up possession.
                            <br />
                            See section 45 and 46 of <em>The Residential Tenancies Act</em>
                        </Typography>
                    </Box>

                    <Box className="soa-info-section">
                        <Box className="soa-info-grid">
                            <Box className="soa-tenant-name-section">
                                <Box className="soa-tenant-row unified-tenant-row">
                                    <Typography variant="body1" className="soa-field-label" sx={{ marginRight: '8px' }}>
                                        TO:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                        <ReadOnlyField
                                            label="Name and Email of Tenant(s)"
                                            value={tenantDisplay}
                                            icon="user"
                                            className="soa-tenant-name"
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box className="soa-address-section">
                                <Box className="soa-address-row">
                                    <Typography variant="body1" className="soa-field-label">AT:</Typography>
                                    <Box className="soa-address-column">
                                        <ReadOnlyField
                                            label="Unit Number"
                                            value={soaData.property_details?.unit_no}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                    <Box className="soa-address-column street">
                                        <ReadOnlyField
                                            label="Street Address"
                                            value={soaData.address?.street}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                    <Box className="soa-address-column">
                                        <ReadOnlyField
                                            label="City"
                                            value={soaData.address?.city}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                    <Box className="soa-address-column">
                                        <ReadOnlyField
                                            label="Province"
                                            value={soaData.address?.province}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                    <Box className="soa-address-column">
                                        <ReadOnlyField
                                            label="Postal Code"
                                            value={soaData.address?.postal_code}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                    <Box className="soa-address-column">
                                        <ReadOnlyField
                                            label="Country"
                                            value={soaData.address?.country}
                                            className="soa-address-field"
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            <Box className="soa-rental-period">
                                <Typography variant="h6" className="soa-section-title">Rental Period</Typography>
                                <Box className="soa-rental-row">
                                    <Typography variant="body1" className="soa-field-label">FROM:</Typography>
                                    <Box className="soa-rental-column">
                                        <ReadOnlyField
                                            label="Start Date"
                                            value={soaData.rental_period?.start_date}
                                            icon="calendar"
                                            className="soa-rental-field"
                                        />
                                    </Box>
                                    <Typography variant="body1" className="soa-field-label">TO:</Typography>
                                    <Box className="soa-rental-column">
                                        <ReadOnlyField
                                            label="End Date"
                                            value={soaData.rental_period?.end_date}
                                            icon="calendar"
                                            className="soa-rental-field"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box className="soa-deposit-section">
                        <Box className="soa-deposit-container">
                            <Typography variant="h6" className="soa-section-title text-center">
                                Security Deposit Summary
                            </Typography>
                            <Paper variant="outlined" className="soa-deposit-summary">
                                <Box className="card-content">
                                    <Box className="card-header">
                                        <Typography variant="body1" className="total-deposit">
                                            Total Security Deposit Received: {formatAmount(soaData.account_details?.security)}
                                        </Typography>
                                    </Box>
                                    {soaData.account_details?.interest !== 0 && (
                                        <Box className="card-header">
                                            <Typography variant="body1" className="interest">
                                                Interest: {formatAmount(soaData.account_details?.interest)}
                                            </Typography>
                                        </Box>
                                    )}
                                    {soaData.account_details?.early_move_out_prorate > 0 && (
                                        <Box className="card-header">
                                            <Typography variant="body1" className="early-move-out-prorate">
                                                Emo Pro Rate: {formatAmount(soaData.account_details.early_move_out_prorate)}
                                            </Typography>
                                        </Box>
                                    )}
                                    <Box className="soa-deductions-section">
                                        <Box className="soa-deductions-container" sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
                                            <Typography variant="h6" className="soa-section-title" sx={{ mb: 2, fontWeight: 'medium' }}>
                                                Less Deductions, as Listed:
                                            </Typography>
                                            <Paper variant="outlined" className="soa-deductions-table" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                                                            <TableCell className="soa-table-header" sx={{ width: '5%', fontWeight: 'bold' }}>
                                                                No.
                                                            </TableCell>
                                                            <TableCell className="soa-table-header" sx={{ width: '30%', fontWeight: 'bold' }}>
                                                                Description
                                                            </TableCell>
                                                            <TableCell className="soa-table-header" sx={{ width: '15%', fontWeight: 'bold' }}>
                                                                Cost Per Action
                                                            </TableCell>
                                                            <TableCell className="soa-table-header" sx={{ width: '10%', fontWeight: 'bold' }}>
                                                                Quantity
                                                            </TableCell>
                                                            <TableCell className="soa-table-header" sx={{ width: '10%', fontWeight: 'bold' }}>
                                                                Total
                                                            </TableCell>
                                                            {showInvoices && (
                                                                <TableCell className="soa-table-header" sx={{ width: '15%', fontWeight: 'bold' }}>
                                                                    Invoice
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {displayDeductions.length > 0 ? (
                                                            displayDeductions.map((deduction, index) => (
                                                                <TableRow key={deduction.id || `deduction-${index}`} className="soa-deduction-row">
                                                                    <TableCell className="soa-table-cell" sx={{ width: '5%' }}>
                                                                        {index + 1}
                                                                    </TableCell>
                                                                    <TableCell className="soa-table-cell" sx={{ width: '30%' }}>
                                                                        {deduction.name || 'N/A'}
                                                                    </TableCell>
                                                                    <TableCell className="soa-table-cell" sx={{ width: '15%' }}>
                                                                        {formatAmount(Number(deduction.cost_per_action) || 0)}
                                                                    </TableCell>
                                                                    <TableCell className="soa-table-cell" sx={{ width: '10%' }}>
                                                                        {Number(deduction.number_of_actions) || 0}
                                                                    </TableCell>
                                                                    <TableCell className="soa-table-cell soa-calculated-total" sx={{ width: '10%' }}>
                                                                        {formatAmount(
                                                                            (Number(deduction.cost_per_action) || 0) * (Number(deduction.number_of_actions) || 1)
                                                                        )}
                                                                    </TableCell>
                                                                    {showInvoices && (
                                                                        <TableCell className="soa-table-cell soa-invoice-cell" sx={{ width: '15%' }}>
                                                                            {deduction.invoice_ref?.invoice_id ? (
                                                                                <DeductionInvoiceRef invoiceRef={deduction.invoice_ref} />
                                                                            ) : deduction.invoice && (typeof deduction.invoice === 'string' ? deduction.invoice !== '' : deduction.invoice.url) ? (
                                                                                <MuiTooltip title="View invoice">
                                                                                    <Badge
                                                                                        badgeContent={<span className="soa-invoice-badge">1</span>}
                                                                                        overlap="circular"
                                                                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                                                    >
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            color="primary"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                handleViewInvoice(index);
                                                                                            }}
                                                                                            aria-label="View uploaded invoice"
                                                                                            sx={{
                                                                                                bgcolor: 'primary.light',
                                                                                                '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' },
                                                                                                transition: 'all 0.2s ease-in-out',
                                                                                                borderRadius: '8px',
                                                                                            }}
                                                                                        >
                                                                                            <AttachFileIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Badge>
                                                                                </MuiTooltip>
                                                                            ) : (
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    No Invoice
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                    )}
                                                                </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={showInvoices ? 6 : 5} sx={{ textAlign: 'center', py: 4 }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No deductions added.
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </Paper>
                                            <Box className="soa-totals-summary">
                                                <Typography variant="body1" className="soa-total-deductions">
                                                    Total Deductions: {formatAmount(totalDeductions)}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    className={`soa-total-balance ${balance >= 0 ? 'positive' : 'negative'}`}
                                                >
                                                    {balance >= 0
                                                        ? `Please return ${formatAmount(Math.abs(balance))} to the Tenant`
                                                        : `Please collect ${formatAmount(Math.abs(balance))} from the Tenant for the Landlord`}
                                                </Typography>
                                            </Box>

                                            {soaData.cheque_enclosed?.status && (
                                                <Box className="soa-payment-section">
                                                    <Box className="soa-payment-row">
                                                        <Box className="soa-payment-option">
                                                            <Box className="soa-cheque-option">
                                                                <Checkbox
                                                                    checked={soaData.cheque_enclosed?.status}
                                                                    className="soa-checkbox"
                                                                    disabled
                                                                    color="primary"
                                                                />
                                                                <Typography variant="body2" className="soa-cheque-text">
                                                                    Cheque enclosed for
                                                                </Typography>
                                                                <Typography variant="body2" className="soa-cheque-amount">
                                                                    {formatAmount(soaData.cheque_enclosed.amount)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box className="soa-landlord-payment">
                                                            <Typography variant="body2" className="soa-landlord-text">
                                                                Please pay Landlord:
                                                            </Typography>
                                                            <Typography variant="body2" className="soa-landlord-amount">
                                                                {formatAmount(balance >= 0 ? balance : 0)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                    </Box>

                    <Box className="soa-footer-section">
                        <Box className="soa-footer-grid">
                            <Box className="soa-footer-left">
                                <Typography variant="h6" className="soa-footer-title">Statement Mailed to</Typography>
                                <Box className="soa-row">
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Name and Email of Tenant(s)"
                                            value={tenantDisplay}
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                                <Box className="soa-row">
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Address"
                                            value={soaData.statement_details?.address}
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                                <Box className="soa-row">
                                    <Typography variant="body2" className="soa-field-label mt-2">Date Mailed:</Typography>
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Date Mailed"
                                            value={soaData.statement_details?.mailed_on}
                                            icon="calendar"
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                            <Box className="soa-footer-right">
                                <Typography variant="h6" className="soa-footer-title">Name and Address of Landlord/Agent:</Typography>
                                <Box className="soa-row">
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Name of Landlord/Agent"
                                            value={soaData.landlord_details?.name}
                                            icon="user"
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                                <Box className="soa-row">
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Address"
                                            value={soaData.landlord_details?.address}
                                            icon="map-pin"
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                                <Box className="soa-row">
                                    <Typography variant="body2" className="soa-field-label mt-2">Phone Number:</Typography>
                                    <Box className="soa-column">
                                        <ReadOnlyField
                                            label="Phone"
                                            value={soaData.landlord_details?.phone}
                                            icon="phone"
                                            className="soa-name"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {soaData.landlord_details?.signature && (
                        <Box className="soa-signature-section">
                            <Box className="soa-section-content" sx={{ padding: '16px' }}>
                                <img
                                    src={soaData.landlord_details.signature}
                                    alt="Landlord Signature"
                                    className="soa-signature-field"
                                    style={{ maxHeight: '100px', maxWidth: '250px', objectFit: 'contain' }}
                                />
                                <Typography variant="caption" className="soa-signature-subtitle">
                                    Signature of Landlord or Landlord's Agent
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </div>

                {showDownloadButtons && (
                    <Container className="d-flex justify-content-center gap-3 my-4">
                        <Button
                            color="primary"
                            onClick={handlePrint}
                            className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                            style={{ minWidth: '150px' }}
                            disabled={showProgress}
                        >
                            <FeatherIcon icon="printer" size={18} />
                            Print SOA
                        </Button>
                        <Button
                            color="success"
                            onClick={handleDownloadPDF}
                            className="d-flex align-items-center gap-2 px-4 py-2 font-weight-bold"
                            style={{ minWidth: '150px' }}
                            disabled={showProgress}
                        >
                            <FeatherIcon icon="download" size={18} />
                            Download PDF
                        </Button>
                    </Container>
                )}
            </Paper>

            {showAttachments && (
                <AttachmentsSOA soaId={soaId} soaData={soaData} token={token} />
            )}
        </Box>
    );
};

export { PreviewSOA };